import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "convex/_generated/api";
import type { Id } from "@/packages/types";
import type { ChecklistSource } from "./types";
import { useTranslation } from "@/packages/utils";

// ─── Option Definitions ───────────────────────────────────────────────────

interface SourceOption {
  value: ChecklistSource;
  label: string;
  icon: string;
  description: string;
  disabled?: boolean;
}

const OFFICIAL_OPTION: SourceOption = {
  value: "official",
  label: "Official Standard",
  icon: "\u2705",
  description: "Use a pre-built checklist from an official standard",
};

const AI_RESEARCH_OPTION: SourceOption = {
  value: "ai_research",
  label: "AI Research",
  icon: "\uD83E\uDD16",
  description: "Let AI research and generate a checklist",
  disabled: true,
};

// ─── Component ────────────────────────────────────────────────────────────

interface ChecklistSourceSelectorProps {
  selectedTemplateId: Id<"auditTemplates"> | null;
  isAuditTypeSelected: boolean;
  value: ChecklistSource | null;
  onChange: (source: ChecklistSource) => void;
}

export function ChecklistSourceSelector({
  selectedTemplateId,
  isAuditTypeSelected,
  value,
  onChange,
}: ChecklistSourceSelectorProps) {
  const { data: hasPublished } = useQuery(
    convexQuery(
      api.admin_standards_queries.hasPublishedStandard,
      selectedTemplateId ? { templateId: selectedTemplateId } : "skip",
    ),
  );

  const { t } = useTranslation(["common", "audits"]);

  // When no audit type is selected, show disabled placeholder
  if (!isAuditTypeSelected) {
    return (
      <div className="rounded-button border border-border/60 bg-card/20 px-4 py-3 text-[13px] text-muted">
        Select an Audit Type first...
      </div>
    );
  }

  const options: SourceOption[] = [OFFICIAL_OPTION, AI_RESEARCH_OPTION];

  const showNoStandardHint =
    value === "official" && (!selectedTemplateId || hasPublished !== true);

  return (
    <div className="flex flex-col gap-2">
      <div
        role="radiogroup"
        aria-label="Checklist source"
        className="flex gap-2"
      >
        {options.map((option) => {
          const isSelected = value === option.value;
          const isDisabled = option.disabled === true;
          return (
            <button
              key={option.value}
              type="button"
              aria-pressed={isSelected}
              aria-label={`${option.label}: ${option.description}`}
              aria-disabled={isDisabled}
              onClick={() => {
                if (!isDisabled) onChange(option.value);
              }}
              className={`flex flex-1 items-center gap-2 rounded-button border px-3 py-2.5 text-[13px] font-medium transition-colors ${
                isDisabled
                  ? "border-border/50 text-muted opacity-50 cursor-not-allowed"
                  : isSelected
                    ? "border-accent bg-accent/10 text-accent cursor-pointer"
                    : "border-border text-secondary hover:border-accent/30 cursor-pointer"
              }`}
            >
              <span className="text-base">{option.icon}</span>
              <span>{option.label}</span>
              {isDisabled && (
                <span className="ml-auto text-[11px] text-muted font-normal">
                  {t("labels.comingSoon")}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {showNoStandardHint && (
        <p className="text-[12px] text-amber-600 dark:text-amber-400">
          {t("audits:wizard.noOfficialStandardAvailable")}
        </p>
      )}
    </div>
  );
}
